import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { IonButton, IonIcon, IonItem, IonLabel } from "@ionic/react";
import { sparklesOutline } from "ionicons/icons";
import React, { useContext, useState } from "react";
import { useAnalytics } from "../../../AppAnalyticsProvider";
import { useAppState } from "../../../AppListenerProvider";
import { AppContext } from "../../../AppStateProvider";
import { UserTrick } from "../../../models/user-trick";
import { UserTrickSearch } from "../../../models/user-trick-search";
import StickerEditor from "../../ComponentsSticker/StickerEditor/StickerEditor";

interface ShareToStoryProps {
    // The clip being shared. Either the full trick (clip page) or the feed
    // metadata shape — both carry the fields Story Studio needs.
    trick: UserTrick | UserTrickSearch;
    // Story Studio is owner-only: only the rider who uploaded a clip can share
    // it to their story (with their tags + earned pins). Non-owners keep the
    // standard share sheet (TrickShare) and never see this entry.
    isOwner: boolean;
    // "icon" → inline action-row button (clip page); "item" → row inside a
    // share popover/menu, sitting above the standard "Send" item.
    variant?: "icon" | "item";
    // Fired whenever the Story Studio modal opens (true) or closes (false). The
    // clip page uses this to mute its background player so it doesn't bleed audio
    // behind the editor (the editor's own clip preview is already muted).
    onOpenChange?: (open: boolean) => void;
}

const ShareToStory: React.FC<ShareToStoryProps> = ({ trick, isOwner, variant = "icon", onOpenChange }) => {
    const { state } = useContext(AppContext);
    const { isNative } = useAppState();
    const { trackEvent } = useAnalytics();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    // Owner gate — the heart of the feature. Render nothing for everyone else.
    if (!isOwner) return null;

    const open = async () => {
        if (isNative) {
            try {
                await Haptics.impact({ style: ImpactStyle.Light });
            } catch {
                // haptics are non-critical
            }
        }
        trackEvent("share_to_story_open", {
            trick_id: (trick as UserTrick)?.trick_id,
            surface: variant,
        });
        onOpenChange?.(true);
        setIsOpen(true);
    };

    return (
        <>
            {variant === "item" ? (
                <IonItem button={true} detail={false} onClick={open}>
                    <IonIcon
                        aria-hidden="true"
                        icon={sparklesOutline}
                        slot="start"
                        style={{ color: "#fff" }}
                    />
                    <IonLabel>Share to your story</IonLabel>
                </IonItem>
            ) : (
                <IonButton
                    onClick={open}
                    fill="clear"
                    size="small"
                    color="dark"
                    aria-label="Share to your story"
                >
                    <IonIcon
                        slot="icon-only"
                        icon={sparklesOutline}
                        className="text-2xl"
                        style={{ color: "#fff" }}
                    />
                </IonButton>
            )}

            <StickerEditor
                isOpen={isOpen}
                onDismiss={() => {
                    setIsOpen(false);
                    onOpenChange?.(false);
                }}
                tags={trick?.tags ?? []}
                sport={trick?.sport ?? ""}
                feature={trick?.terrain ?? ""}
                thumbnail={trick?.thumbnail ?? ""}
                username={state.user?.username ?? ""}
                badgeIds={state.user?.profile?.badges ?? []}
                videoUrl={trick?.video}
                trickName={trick?.trick_name}
                location={trick?.location}
            />
        </>
    );
};

export default ShareToStory;
